import {useEffect, useId, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Button, Modal, Spinner} from '@goproblems/ui';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faBolt, faTrophy} from '@fortawesome/free-solid-svg-icons';
import {useReducedMotion} from 'framer-motion';
import {useLottie} from 'lottie-react';
import {useTranslation} from 'react-i18next';
import {getLevelName} from '@utils/elo';
import victoryAnimation from '../../../animations/victory.json';

type LevelCompletedModalProps = {
    level: number;
    speed: boolean;
    hasNextLevel: boolean;
    onNext: () => Promise<void>;
    onHide: () => void;
};

export const LevelCompletedModal = ({
    level,
    speed,
    hasNextLevel,
    onNext,
    onHide,
}: LevelCompletedModalProps) => {
    const {t} = useTranslation();
    const titleId = useId();
    const descriptionId = useId();
    const dialogRef = useRef<HTMLDivElement>(null);
    const nextButtonRef = useRef<HTMLElement>(null);
    const stayButtonRef = useRef<HTMLElement>(null);
    const advancingRef = useRef(false);
    const [advancing, setAdvancing] = useState(false);
    const [animationComplete, setAnimationComplete] = useState(false);
    const shouldReduceMotion = useReducedMotion();
    const {View} = useLottie(
        {
            animationData: victoryAnimation,
            loop: false,
            autoplay: !shouldReduceMotion,
            rendererSettings: {preserveAspectRatio: 'xMidYMid slice'},
            onComplete: () => setAnimationComplete(true),
        },
        {width: '100%', height: '100%'}
    );

    useEffect(() => {
        const previousFocus = document.activeElement;
        (nextButtonRef.current ?? stayButtonRef.current)?.focus();

        const trapFocus = (event: KeyboardEvent) => {
            if (event.key !== 'Tab') return;
            const buttons = dialogRef.current?.querySelectorAll<HTMLElement>(
                'button:not(:disabled)'
            );
            if (!buttons?.length) return;
            const first = buttons[0];
            const last = buttons[buttons.length - 1];
            const active = document.activeElement;
            if (
                event.shiftKey &&
                (active === first || !dialogRef.current?.contains(active))
            ) {
                event.preventDefault();
                last.focus();
            } else if (
                !event.shiftKey &&
                (active === last || !dialogRef.current?.contains(active))
            ) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', trapFocus);
        return () => {
            document.removeEventListener('keydown', trapFocus);
            if (
                previousFocus instanceof HTMLElement &&
                previousFocus.isConnected
            ) {
                previousFocus.focus();
            }
        };
    }, []);

    useEffect(() => {
        if (!hasNextLevel) stayButtonRef.current?.focus();
    }, [hasNextLevel]);

    const handleNext = async () => {
        if (advancingRef.current) return;
        advancingRef.current = true;
        setAdvancing(true);
        try {
            await onNext();
        } finally {
            advancingRef.current = false;
            setAdvancing(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[1050] isolate bg-neutral-950/55">
            {!shouldReduceMotion && !animationComplete && (
                <div
                    className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
                    aria-hidden="true">
                    {View}
                </div>
            )}
            <Modal
                ref={dialogRef}
                show
                centered
                onHide={onHide}
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
                className="!z-10 !bg-transparent"
                dialogClassName="!max-w-md !overflow-hidden">
                <Modal.Header closeButton>
                    <Modal.Title id={titleId}>
                        {t('single_problem.level_completed.title')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-6 py-8 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-3xl text-amber-600 dark:bg-amber-400/15 dark:text-amber-400">
                        <FontAwesomeIcon
                            icon={speed ? faBolt : faTrophy}
                            aria-hidden="true"
                        />
                    </div>
                    <h2 className="mb-3 text-2xl font-bold">
                        {t(
                            speed
                                ? 'single_problem.level_completed.speed_heading'
                                : 'single_problem.level_completed.heading',
                            {level: getLevelName(level)}
                        )}
                    </h2>
                    <p
                        id={descriptionId}
                        className="m-0 text-neutral-600 dark:text-neutral-300">
                        {t(
                            speed
                                ? 'single_problem.level_completed.speed_description'
                                : 'single_problem.level_completed.description'
                        )}
                    </p>
                </Modal.Body>
                <Modal.Footer className="flex-col-reverse sm:flex-row">
                    <Button
                        ref={stayButtonRef}
                        variant="outline-secondary"
                        className="w-full sm:w-auto"
                        onClick={onHide}>
                        {t('single_problem.level_completed.stay')}
                    </Button>
                    {hasNextLevel && (
                        <Button
                            ref={nextButtonRef}
                            variant="success"
                            className="w-full gap-2 sm:w-auto"
                            disabled={advancing}
                            onClick={() => void handleNext()}>
                            {advancing && <Spinner size="sm" />}
                            {t('single_problem.level_completed.continue')}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>,
        document.body
    );
};
